import { BasicSymbolType } from '../../../core/reel/symbol/config/IReelSymbolStruct';
import { ArrayHelper } from '../../../core/utils/ArrayHelper';
import { BaseServerData, SanJumpData, SanJumpingWilds, stickyFS } from '../base/IData';
import { CoreData } from "./CoreData";

export class ReelGameData extends CoreData {
    
    protected _reelsNextSpin: string;
    protected _currentWindowReels: string[][] = [];
    protected _currentEvalReels: string[][] = [];
    protected _savedStickies: stickyFS[] = [];
    protected _sanJump: SanJumpData[] = [];
    protected _specialSplits: boolean[][] = [];
    protected _revealerTopSplits: boolean[] = [];
    protected _revealerBotSplits: boolean[] = [];
    protected _stopBarsOnStop: boolean[] = [];

    public get raw(): BaseServerData {
        return this.data;
    }

    private filterOffsetFromArray(arr: string[][], size: number[]): string[][] {
        if (!arr) return [];
        return arr.map((r, reel) => r.map((w, row) => {
            if (row == 0 || size[reel] < row) return "";
            return w;
        }).filter(v => !!v));
    }

    public get windowReel(): string[][] {
        return this._currentWindowReels;
    }

    public get convictedMysteryMultiplier(): number {
        return this.data.convictedMysteryMultiplier ?? 0;
    }

    public get convictedMysterySymbol(): string {
        return this.data.convictedMysterySymbol ?? "";
    }

    public get evalReel(): string[][] {
        return this._currentEvalReels;
    }

    public get reels(): string[][] {
        return this.data.reels;
    }

    public get reelsNextSpin(): string {
        return this._reelsNextSpin = this.data.nextMode ?? this.data.mode;
    }

    public get amountOfScattersLanded(): number {
        return ArrayHelper.countWithCondition(this.scatterPositions, (v) => v >= 0);
    }

    public get scatterHaveLanded(): boolean {
        return !!this.amountOfScattersLanded;
    }

    public get setupPositions(): number[][] {
        return this.fetchSymbolPos("Z", this.windowReel, false) as number[][];
    }

    public get scatterPositions(): number[] {
        return this.fetchSymbolPos(BasicSymbolType.SCATTER, this.windowReel, true) as number[];
    }

    public get allMysteryPos(): number[][] {
        return this.fetchSymbolPos(BasicSymbolType.MYSTERY, this.windowReel, false) as number[][];
    }

    public get mysteryPos(): number[] {
        return this.fetchSymbolPos(BasicSymbolType.MYSTERY, this.windowReel, true) as number[];
    }

    public get mysteryRevealSymbol(): string {
        if (!this.evaluateArea.length) return "";
        let mystSym = "";
        this.data.topBottomReelOpened.forEach((v, reel) => {
            if (this.data.bottomReel[reel] == "XWAYS" && this.data.topReel[reel] == "XWAYS") return;
            if (v && !mystSym) {
                if (this.data.topReel[reel] == "XWAYS") {
                    mystSym = this.evaluateArea[reel][0];
                } else if (this.data.bottomReel[reel] == "XWAYS") {
                    mystSym = this.evaluateArea[reel][this.evaluateArea[reel].length - 1];
                }
            }
        });
        return mystSym;
    }

    public get wildSinglePos(): number[] {
        return this.fetchSymbolPos(BasicSymbolType.WILD, this.windowReel, true) as number[];
    }

    public get specialSplits(): boolean[][] {
        return this._specialSplits ?? [];
    }

    public get revealerTopSplits(): boolean[] {
        return this._revealerTopSplits ?? [];
    }

    public get revealerBotSplits(): boolean[] {
        return this._revealerBotSplits ?? [];
    }

    public get gotMystery(): boolean {
        return !!this.mysteryPos.filter(v => v >= 0).length;
    }

    public get WildPositions(): number[][] {
        return this.fetchSymbolPos(BasicSymbolType.WILD, this.windowReel, false) as number[][];
    }

    public get amountOfScatters(): number {
        return this.countTotalAmountOfType(BasicSymbolType.SCATTER, this.windowReel);
    }

    public get symbolSizeMap(): number[][] {
        return this.symbolSizes ?? ArrayHelper.countAmountOfIndexes(this.windowReel);
    }

    public get totalPossibleWays(): number {
        return this.data.totalWays ?? 1024;
    }

    public get symbolSizes(): number[][] {
        return this.data.symbolSizes ?? [[]];
    }

    public get wildBoolMap(): boolean[][] {
        return this.symbolBooleanPos(BasicSymbolType.WILD, this.evalReel);
    }

    public get reelModeHeight(): number {
        return Math.max(this.symbolSizes[0].length, 4);
    }

    public get gameAreaSize(): number[] {
        return ArrayHelper.initArrayWithValues(this.data.reels.length, (i) => 4) ?? [];
    }

    public get evaluateArea(): string[][] {
        return this.data.evaluatedArea ?? [];
    }

    public get altEvaluateArea(): string[][] {
        return this.data.alternativeEvaluatedReels ?? [];
    }

    public get StopBarsOnStop(): boolean[] {
        return this._stopBarsOnStop;
    }

    public get jumpData(): SanJumpData[] {
        return this._sanJump;
    }

    public get lastStickies(): stickyFS[] {
        return this._savedStickies;
    }

    apply(data: any): void {
        super.apply(data);
        const gameAreaSize = this.gameAreaSize;
        this._currentWindowReels = this.filterOffsetFromArray(this.data.reels, gameAreaSize);
        this._currentEvalReels = this.filterOffsetFromArray(this.data.evaluatedArea, gameAreaSize);
        this._sanJump = this.data.jumpingWilds ? this.sanQ2JumpStuff(this.data.jumpingWilds) : this.jumpDecryption(this.data.stickyWildPositions);
        this._stopBarsOnStop = this.defineBarStop();
        
        this.reels.forEach((reel, reelIndex) => {
            reel.forEach((val, rowIndex) => {
                if (val === "XWAYS") this.reels[reelIndex][rowIndex] = "M2";
                if (val === "MYSTERY_CONVICT") this.reels[reelIndex][rowIndex] = "Z";
            });
        });

        this._specialSplits = this.fetchSpecialSplits();
    }

    defineBarStop(): boolean[] {
        return ArrayHelper.initArrayWithValues(this.data.topBottomReelOpened.length, (i) => {
            return this.data.topBottomReelOpened[i] && !(this.scatterPositions[i] >= 0);
        });
    }

    protected sanQ2JumpStuff(data: SanJumpingWilds[]): SanJumpData[] {
        return data.map(val => ({
            from: {
                reel: val.originalReelPos,
                row: val.originalReelRow,
                multiplier: val.originalMultiplier
            },
            to: {
                reel: val.newReelPos,
                row: val.newRowPos,
                multiplier: val.newMultiplier
            }
        }));
    }

    fetchSpecialSplits(): boolean[][] {
        let map: boolean[][] = [];

        if (!(this.data.topReel && this.data.bottomReel)) return map;

        const willSplit = ArrayHelper.initArrayWithValues(this.data.topReel.length, (v) => {
            return (this.data.topReel[v] == "XS" || this.data.bottomReel[v] == "XS") && this.data.topBottomReelOpened[v];
        })

        if (!(willSplit.find((v) => v))) return map; // If there is no splits

        map = ArrayHelper.initArrayWithValues(
            this.windowReel.length, (i) => ArrayHelper.initArrayWithValues(this.windowReel[i].length, () => false))
        
        const wildMap = this.wildBoolMap;

        for (let reel = 0; reel < willSplit.length; reel++) {
            if (willSplit[reel]) {
                for (let row = 0; row < wildMap[reel].length; row++) {
                    map.forEach((m,i) => m[row] = this.symbolSizes[i][row+1] > 1);
                }
            }
        }
        return map;
    }
}